import React, { useState } from "react";
import {
	ShowConfirmDialogFunc,
	ConfirmDialogContext,
} from "src/components/common/dialogs/confirmDialog/ConfirmDialogProvider.tsx";
import { AavoIconButton } from "src/components/common/buttons/AavoIconButton";
import {
	faPenToSquare,
	faPlus,
	faRefresh,
	faSave,
	faTrash,
} from "@fortawesome/pro-regular-svg-icons";
import { ErrorDialog } from "src/components/common/dialogs/errorDialog/ErrorDialog.tsx";
import { AnyError } from "src/errorHandling/anyError";
import { logError } from "src/errorHandling/errorLogging";
import { IconDefinition } from "@fortawesome/fontawesome-svg-core";
import {
	CrudActionButtonDefinition,
	EnabledActionButtonDefinition,
} from "src/components/common/crud/crudActionButtonDefinition";

import { useContextOrThrow } from "src/utils/useContextOrThrow.tsx";

const DEFAULT_ACTION: CrudActionButtonDefinition<unknown, unknown> = {
	type: "hidden",
};

export type CrudDataGridActionButtonsProps = {
	refresh?: CrudActionButtonDefinition;
	save?: CrudActionButtonDefinition;
	edit?: CrudActionButtonDefinition;
	add?: CrudActionButtonDefinition;
	remove?: CrudActionButtonDefinition;
};

export const CrudActionButtons = ({
	refresh = DEFAULT_ACTION,
	save = DEFAULT_ACTION,
	edit = DEFAULT_ACTION,
	add = DEFAULT_ACTION,
	remove = DEFAULT_ACTION,
}: CrudDataGridActionButtonsProps) => {
	const [error, setError] = useState<AnyError | undefined>(undefined);
	const [allButtonsDisabled, setAllButtonsDisabled] = useState<boolean>(false);
	return (
		<>
			<ErrorDialog
				error={error}
				onClose={() => {
					setError(undefined);
				}}
			/>
			<ActionButton
				action={refresh}
				icon={faRefresh}
				allButtonsDisabled={allButtonsDisabled}
				setAllButtonsDisabled={setAllButtonsDisabled}
				setError={setError}
			/>
			<ActionButton
				action={edit}
				icon={faPenToSquare}
				allButtonsDisabled={allButtonsDisabled}
				setAllButtonsDisabled={setAllButtonsDisabled}
				setError={setError}
			/>
			<ActionButton
				action={add}
				icon={faPlus}
				allButtonsDisabled={allButtonsDisabled}
				setAllButtonsDisabled={setAllButtonsDisabled}
				setError={setError}
			/>
			<ActionButton
				action={save}
				icon={faSave}
				allButtonsDisabled={allButtonsDisabled}
				setAllButtonsDisabled={setAllButtonsDisabled}
				setError={setError}
			/>
			<ActionButton
				action={remove}
				icon={faTrash}
				allButtonsDisabled={allButtonsDisabled}
				setAllButtonsDisabled={setAllButtonsDisabled}
				setError={setError}
				confirmByDefault={true}
			/>
		</>
	);
};

const ActionButton = ({
	action,
	icon,
	allButtonsDisabled,
	setAllButtonsDisabled,
	setError,
	confirmByDefault = false,
}: {
	action: CrudActionButtonDefinition | undefined;
	icon: IconDefinition;
	allButtonsDisabled: boolean;
	setAllButtonsDisabled: React.Dispatch<React.SetStateAction<boolean>>;
	setError: (e: AnyError | undefined) => void;
	confirmByDefault?: boolean;
}) => {
	const confirmFunction = useContextOrThrow(ConfirmDialogContext);
	const [loading, setLoading] = useState<boolean>(false);

	if (action === undefined) return <></>;

	const onClick = async (e: React.MouseEvent) => {
		if (action.type !== "enabled") return;

		e.preventDefault();
		setLoading(true);
		setAllButtonsDisabled(true);
		try {
			// Confirm rejection does not cause error, just prevent the actual action
			const confirmed = await confirmAction(action, confirmFunction, confirmByDefault);
			if (!confirmed) return;

			await action.action();
		} catch (e) {
			setError(e as AnyError);
			logError(e as AnyError);
		} finally {
			setLoading(false);
			setAllButtonsDisabled(false);
		}
	};

	if (action.type === "hidden") return null;

	return (
		<AavoIconButton
			tooltip={action?.type === "disabled" ? action.tooltip : ""}
			color={"primary"}
			icon={icon}
			loading={loading}
			disabled={action?.type === "disabled" || allButtonsDisabled}
			onClick={onClick}
		/>
	);
};

const confirmAction = async <ActionParams = unknown, ActionResult = unknown>(
	action: EnabledActionButtonDefinition<ActionParams, ActionResult>,
	confirmFunction: ShowConfirmDialogFunc,
	confirmByDefault: boolean,
): Promise<boolean> => {
	if (action.confirm === undefined) {
		if (confirmByDefault) return await confirmFunction();
		else return true;
	} else if (action.confirm === false) {
		return true;
	} else if (action.confirm === true) {
		return await confirmFunction();
	} else {
		return await confirmFunction(action.confirm);
	}
};
